import { useState, useMemo, useCallback, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { LockIcon } from './icons/LockIcon'
import { WarningIcon } from './icons/WarningIcon'
import { ChevronsIcon } from './icons/ChevronsIcon'
import { Toggle } from './Toggle'

/* ─── Contract ─── */

export type OperationsTreeMode = 'tenant' | 'template' | 'user' | 'agent'

export interface OperationsTreeOp {
  operationId: string
  key: string
  name: string
  category: string
  type: 'Read' | 'Execute'
}

export interface OperationsTreeProps {
  ops: OperationsTreeOp[]
  mode: OperationsTreeMode
  isChecked: (operationId: string) => boolean
  onToggle: (operationId: string, checked: boolean) => void
  isTenantEnabled?: (operationId: string) => boolean
  disabled?: boolean
  ariaLabel: string
  /** Aria-label for a leaf toggle. Defaults to the operation's plain name. */
  toggleAriaLabel?: (op: OperationsTreeOp) => string
  /** Aria-label for a category's toggle-all switch. Defaults to the plain category name. */
  categoryToggleAriaLabel?: (category: string) => string
  /** Extra inline content appended to a leaf row's label (badges, hints). */
  renderRowExtra?: (op: OperationsTreeOp) => React.ReactNode
  /** Additional per-row disable predicate, independent of the tenant-disabled lock. */
  isRowDisabled?: (op: OperationsTreeOp) => boolean
  /**
   * Operations rendered as a `Deprecated` group pinned above the categories. They are an
   * ordinary group in every other respect (prototype `grouped()`, agent-studio.html:7449-7461):
   * a working category toggle, counted in the enabled/total overview, and swept by the quick
   * actions — R-EDIT-4 requires an admin to be able to disable them manually.
   */
  deprecatedOps?: OperationsTreeOp[]
  /** Label content for the deprecated group's parent row. */
  deprecatedGroupLabel?: React.ReactNode
  /** Copy overrides — each falls back to the Mambu MCP wording when omitted. */
  copy?: OperationsTreeCopyOverrides
  /** Hides the "Execute" type-filter chip — for read-only agents whose `ops` never contain Execute operations. */
  hideExecuteFilter?: boolean
}

export interface OperationsTreeCopyOverrides {
  filtering?: string
  typeFilter?: string
  statusFilter?: string
  quickActions?: string
  readOnlyPreset?: string
  enableShown?: string
  disableShown?: string
  overview?: string
  /**
   * The whole enabled/total sentence as ONE parameterised message (spec §23 — the count
   * must not be assembled from fragments at the call site). Return a node so the message
   * can bold the enabled figure via <Trans>.
   */
  countEnabled?: (enabled: number, total: number) => React.ReactNode
  noMatch?: string
  lockedTooltip?: string
  deprecatedTooltip?: string
}

type TypeFilter = 'All' | 'Read' | 'Execute'
type StatusFilter = 'All' | 'Enabled' | 'Disabled'

const DEPRECATED_GROUP_KEY = '__deprecated__'

/**
 * Fixed 'en' collator. Operation and category names are catalogue metadata and are never
 * translated (spec §24), so the tree must sort identically under every UI locale — a
 * bare `localeCompare()` would reorder the tree when the admin switches language.
 */
const NAME_COLLATOR = new Intl.Collator('en')

function sortOpsByName(ops: OperationsTreeOp[]): OperationsTreeOp[] {
  return [...ops].sort((a, b) => NAME_COLLATOR.compare(a.name, b.name))
}

/**
 * Categories A–Z, operations A–Z within each category (spec §9-ord). Nothing is promoted,
 * pinned or floated by state — not disabled, not tenant-locked, not newly discovered: a
 * predictable position matters more than surfacing a subset, because admins scan a
 * ~330-operation tree by name. State is conveyed by badges and toggles only.
 */
function groupByCategory(ops: OperationsTreeOp[]): Map<string, OperationsTreeOp[]> {
  const grouped = new Map<string, OperationsTreeOp[]>()
  for (const op of ops) {
    const cat = op.category || 'Other'
    if (!grouped.has(cat)) grouped.set(cat, [])
    grouped.get(cat)!.push(op)
  }
  const sorted = new Map<string, OperationsTreeOp[]>()
  for (const cat of [...grouped.keys()].sort(NAME_COLLATOR.compare)) {
    sorted.set(cat, sortOpsByName(grouped.get(cat)!))
  }
  return sorted
}

/**
 * Wraps each case-insensitive match of `term` in <mark>, as React nodes. Deliberately not
 * innerHTML: operation names are catalogue-sourced text and must never be interpolated as
 * markup (prototype does this with `hl()`, agent-studio.html:7423).
 */
function Highlighted({ text, term }: { text: string; term: string }): JSX.Element {
  if (!term) return <>{text}</>
  const haystack = text.toLowerCase()
  const needle = term.toLowerCase()
  const parts: React.ReactNode[] = []
  let cursor = 0
  let key = 0
  for (;;) {
    const at = haystack.indexOf(needle, cursor)
    if (at === -1) {
      parts.push(text.slice(cursor))
      break
    }
    if (at > cursor) parts.push(text.slice(cursor, at))
    parts.push(<mark key={key++}>{text.slice(at, at + needle.length)}</mark>)
    cursor = at + needle.length
  }
  return <>{parts}</>
}

/** Strip trailing period and ensure sentence case for display. */
function normalizeName(name: string): string {
  const s = name.trimEnd().replace(/\.$/, '').trimEnd()
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)
}

export function OperationsTree(props: OperationsTreeProps): JSX.Element {
  const {
    ops, mode, isChecked, onToggle, isTenantEnabled, disabled = false, ariaLabel,
    toggleAriaLabel, categoryToggleAriaLabel, renderRowExtra, isRowDisabled,
    deprecatedOps = [], deprecatedGroupLabel, copy: copyOverrides, hideExecuteFilter = false,
  } = props
  const { t } = useTranslation()
  const copy = {
    filtering: copyOverrides?.filtering ?? t('mambuMcpDetail.opsTree.filtering'),
    typeFilter: copyOverrides?.typeFilter ?? t('mambuMcpDetail.opsTree.typeFilter'),
    statusFilter: copyOverrides?.statusFilter ?? t('mambuMcpDetail.opsTree.statusFilter'),
    quickActions: copyOverrides?.quickActions ?? t('mambuMcpDetail.opsTree.quickActions'),
    readOnlyPreset: copyOverrides?.readOnlyPreset ?? t('mambuMcpDetail.opsTree.readOnlyPreset'),
    enableShown: copyOverrides?.enableShown ?? t('mambuMcpDetail.opsTree.enableShown'),
    disableShown: copyOverrides?.disableShown ?? t('mambuMcpDetail.opsTree.disableShown'),
    overview: copyOverrides?.overview ?? t('mambuMcpDetail.opsTree.overview'),
    countEnabled: copyOverrides?.countEnabled ?? ((enabled: number, total: number) => (
      <Trans
        i18nKey="mambuMcpDetail.opsTree.countEnabled"
        values={{ enabled, total }}
        components={[<strong key="enabled" />]}
      />
    )),
    noMatch: copyOverrides?.noMatch ?? t('mambuMcpDetail.opsTree.noMatch'),
    lockedTooltip: copyOverrides?.lockedTooltip ?? t('mambuMcpDetail.opsTree.lockedTooltip'),
    deprecatedTooltip: copyOverrides?.deprecatedTooltip ?? t('mambuMcpDetail.opsTree.deprecatedTooltip'),
    searchPlaceholder: t('mambuMcpDetail.opsTree.searchPlaceholder'),
  }
  const typeFilterLabels: Record<TypeFilter, string> = {
    All: t('mambuMcpDetail.opsTree.optionAll'),
    Read: t('mambuMcpDetail.opsTree.optionRead'),
    Execute: t('mambuMcpDetail.opsTree.optionExecute'),
  }
  const statusFilterLabels: Record<StatusFilter, string> = {
    All: t('mambuMcpDetail.opsTree.optionAll'),
    Enabled: t('mambuMcpDetail.opsTree.optionEnabled'),
    Disabled: t('mambuMcpDetail.opsTree.optionDisabled'),
  }
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('All')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All')
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set())
  const treeRef = useRef<HTMLDivElement>(null)

  // The input keeps whatever the admin typed; matching and highlighting use the trimmed
  // term so a trailing space doesn't blank the tree (prototype trims too, line 7583).
  const term = search.trim()

  const isLocked = useCallback(
    (op: OperationsTreeOp) => mode !== 'tenant' && isTenantEnabled?.(op.operationId) === false,
    [mode, isTenantEnabled],
  )

  const opEnabled = useCallback(
    (op: OperationsTreeOp) => isChecked(op.operationId) && !isLocked(op),
    [isChecked, isLocked],
  )

  const matchesFilters = useCallback((op: OperationsTreeOp) => {
    if (term && !op.name.toLowerCase().includes(term.toLowerCase())) return false
    if (typeFilter !== 'All' && op.type !== typeFilter) return false
    if (statusFilter === 'Enabled' && !opEnabled(op)) return false
    if (statusFilter === 'Disabled' && opEnabled(op)) return false
    return true
  }, [term, typeFilter, statusFilter, opEnabled])

  const handleTreeKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(e.key)) return
    const tree = treeRef.current
    if (!tree) return
    const focused = document.activeElement as HTMLElement
    if (!tree.contains(focused)) return
    const items = Array.from(tree.querySelectorAll<HTMLElement>('[role="treeitem"]'))
    const idx = items.indexOf(focused)
    if (idx === -1) return
    e.preventDefault()
    // Expansion lives on the caret, not the row (prototype `[data-exp]`, line 7595-7603),
    // so keyboard expand/collapse drives the caret the pointer would have hit.
    const caret = () => focused.querySelector<HTMLElement>('[data-exp]')
    switch (e.key) {
      case 'ArrowDown':
        items[idx + 1]?.focus()
        break
      case 'ArrowUp':
        items[idx - 1]?.focus()
        break
      case 'ArrowRight': {
        const isExpanded = focused.getAttribute('aria-expanded')
        if (isExpanded === 'false') caret()?.click()
        else if (isExpanded === 'true') items[idx + 1]?.focus()
        break
      }
      case 'ArrowLeft': {
        const isExpanded = focused.getAttribute('aria-expanded')
        if (isExpanded === 'true') caret()?.click()
        break
      }
      case 'Enter':
      case ' ':
        caret()?.click()
        break
    }
  }, [])

  const filteredOps = useMemo(() => ops.filter(matchesFilters), [ops, matchesFilters])
  const filteredDeprecatedOps = useMemo(
    () => sortOpsByName(deprecatedOps.filter(matchesFilters)),
    [deprecatedOps, matchesFilters],
  )

  const filteredGrouped = useMemo(() => groupByCategory(filteredOps), [filteredOps])

  /* ── Counting ──
   * Deprecated operations are part of the data set, exactly as they are in the prototype's
   * `state.data` (agent-studio.html:7508-7510): they count toward the overview and are swept
   * by the quick actions, because R-EDIT-4 requires the admin to be able to turn them off.
   */
  const allOps = useMemo(() => [...ops, ...deprecatedOps], [ops, deprecatedOps])
  const shownOps = useMemo(
    () => [...filteredOps, ...filteredDeprecatedOps],
    [filteredOps, filteredDeprecatedOps],
  )
  const enabledCount = useMemo(() => allOps.filter(op => opEnabled(op)).length, [allOps, opEnabled])
  const activeCount = allOps.length

  /* ── Filters ── */
  const anyFilterActive = term !== '' || typeFilter !== 'All' || statusFilter !== 'All'
  function clearFilters() {
    setSearch('')
    setTypeFilter('All')
    setStatusFilter('All')
  }

  /* ── Quick actions ── */
  function applyReadOnlyPreset() {
    if (disabled) return
    for (const op of allOps) {
      if (isLocked(op)) continue
      const target = op.type === 'Read'
      if (isChecked(op.operationId) !== target) onToggle(op.operationId, target)
    }
  }
  function enableShown() {
    if (disabled) return
    for (const op of shownOps) {
      if (isLocked(op) || isChecked(op.operationId)) continue
      onToggle(op.operationId, true)
    }
  }
  function disableShown() {
    if (disabled) return
    for (const op of shownOps) {
      if (isLocked(op) || !isChecked(op.operationId)) continue
      onToggle(op.operationId, false)
    }
  }

  /* ── Groups: the pinned Deprecated group first, then categories A–Z ── */
  const groups = useMemo(() => {
    const list: { key: string; ops: OperationsTreeOp[]; deprecated: boolean }[] = []
    if (filteredDeprecatedOps.length > 0) {
      list.push({ key: DEPRECATED_GROUP_KEY, ops: filteredDeprecatedOps, deprecated: true })
    }
    for (const [category, catOps] of filteredGrouped.entries()) {
      list.push({ key: category, ops: catOps, deprecated: false })
    }
    return list
  }, [filteredGrouped, filteredDeprecatedOps])

  /* ── Collapse ── */
  const visibleGroupKeys = useMemo(() => groups.map(g => g.key), [groups])

  // An active search term force-expands every group, so a collapsed category can never
  // hide its own matches (prototype: `isExp = expanded.has(cat) || !!search`, line 7521).
  const isCategoryCollapsed = useCallback(
    (key: string) => term === '' && collapsedCats.has(key),
    [term, collapsedCats],
  )
  // Derived, not a latch: collapsing a single category immediately flips the control to
  // "Expand All" (prototype `anyCollapsed`, line 7497).
  const anyCollapsed = visibleGroupKeys.some(isCategoryCollapsed)

  function toggleCollapseAll() {
    setCollapsedCats(anyCollapsed ? new Set() : new Set(visibleGroupKeys))
  }
  function toggleCategory(cat: string) {
    setCollapsedCats(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  /* ── Category helpers ── */
  function toggleCategoryOps(catOps: OperationsTreeOp[], enable: boolean) {
    if (disabled) return
    for (const op of catOps) {
      if (isLocked(op)) continue
      if (isChecked(op.operationId) !== enable) onToggle(op.operationId, enable)
    }
  }

  /* ── Rows ──
   * Built imperatively so a single zebra index can advance across EVERY rendered row,
   * parents included, the way the prototype's one `zebraIdx` does (agent-studio.html:7532
   * for parents, 7557 for leaves). Row 0 is unstriped.
   */
  let zebraIdx = 0
  const nextIsZebra = () => zebraIdx++ % 2 === 1

  function renderLeaf(op: OperationsTreeOp, opts: { locked: boolean; deprecated: boolean }) {
    const { locked, deprecated } = opts
    const checked = isChecked(op.operationId)
    const opType = op.type.toLowerCase() as 'read' | 'execute'
    const rowDisabled = disabled || locked || (isRowDisabled?.(op) ?? false)
    const isZebra = nextIsZebra()
    return (
      <div
        key={op.operationId}
        className={`ops-tree-row leaf${isZebra ? ' zebra' : ''}${locked ? ' op-locked' : ''}${deprecated ? ' deprecated' : ''}`}
        style={{ paddingLeft: 34 }}
      >
        <span className="ops-tree-caret leaf" aria-hidden="true">·</span>
        <span className="ops-tree-lbl">
          {locked && (
            <span className="ops-tree-tenant-lock" title={copy.lockedTooltip}>
              <LockIcon size={12} />
            </span>
          )}
          <span className="t"><Highlighted text={normalizeName(op.name)} term={term} /></span>
          <span className={`badge badge-${opType}`}>{typeFilterLabels[op.type]}</span>
          {deprecated && (
            <span className="ops-tree-dep-icon" title={copy.deprecatedTooltip}>
              <WarningIcon size={13} />
            </span>
          )}
          {renderRowExtra?.(op)}
        </span>
        <Toggle
          checked={checked}
          onChange={(v) => onToggle(op.operationId, v)}
          label={toggleAriaLabel ? toggleAriaLabel(op) : op.name}
          disabled={rowDisabled}
          title={locked ? copy.lockedTooltip : undefined}
        />
      </div>
    )
  }

  const treeRows: React.ReactNode[] = []

  for (const group of groups) {
    const { key, ops: catOps, deprecated } = group
    // The deprecated group's key is a sentinel, so its accessible name uses the group word.
    const groupName = deprecated ? t('mambuMcpDetail.opsTree.deprecatedGroup') : key
    const isCollapsed = isCategoryCollapsed(key)
    const togglable = catOps.filter(op => !isLocked(op))
    const catEnabled = togglable.filter(op => isChecked(op.operationId)).length
    const catActive = togglable.length
    const isPartial = catEnabled > 0 && catEnabled < catActive

    treeRows.push(
      <div
        key={`cat:${key}`}
        role="treeitem"
        aria-expanded={!isCollapsed}
        data-tree-category={key}
        tabIndex={0}
        className={`ops-tree-row ops-tree-parent${nextIsZebra() ? ' zebra' : ''}${deprecated ? ' ops-tree-deprecated-cat' : ''}`}
        style={{ paddingLeft: 10 }}
      >
        <span
          className="ops-tree-caret"
          data-exp={key}
          aria-hidden="true"
          onClick={() => toggleCategory(key)}
        >
          {isCollapsed ? '▸' : '▾'}
        </span>
        <span className="ops-tree-lbl">
          {deprecated
            ? (
              <span className="t" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {deprecatedGroupLabel}
              </span>
            )
            : <span className="t"><Highlighted text={key} term={term} /></span>}
          <span className={`ops-tree-sub${isPartial ? ' partial' : ''}`}>
            {t('mambuMcpDetail.opsTree.categoryCount', { enabled: catEnabled, total: catActive })}
          </span>
        </span>
        {/* Binary, never mid-way: partial renders ON so a click disables the whole category
            (prototype line 7528-7530 and 7614-7615). The partial fact is carried by the
            adjacent amber X/Y pill, which is real text in the row's accessible name. */}
        <Toggle
          checked={catEnabled > 0}
          onChange={() => toggleCategoryOps(catOps, catEnabled === 0)}
          label={categoryToggleAriaLabel ? categoryToggleAriaLabel(groupName) : groupName}
          disabled={disabled || catActive === 0}
        />
      </div>,
    )
    if (!isCollapsed) {
      for (const op of catOps) {
        treeRows.push(renderLeaf(op, { locked: isLocked(op), deprecated }))
      }
    }
  }

  const isEmpty = shownOps.length === 0

  return (
    <div className={`ops-tree-wrap${disabled ? ' ops-tree-ro' : ''}`}>

      {/* ── Filtering ── */}
      <div className="ops-tree-section">
        <label className="form-label">{copy.filtering}</label>
        <div className="ops-tree-toolbar">
          <input
            type="text"
            className="ops-tree-search"
            placeholder={copy.searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span className="ops-tree-seg-label">{copy.typeFilter}</span>
          <div className="ops-tree-seg">
            {(['All', 'Read', 'Execute'] as TypeFilter[])
              .filter(tf => tf !== 'Execute' || !hideExecuteFilter)
              .map(tf => (
              <button key={tf} type="button" className={typeFilter === tf ? 'on' : ''} onClick={() => setTypeFilter(tf)}>{typeFilterLabels[tf]}</button>
            ))}
          </div>
          <span className="ops-tree-seg-label">{copy.statusFilter}</span>
          <div className="ops-tree-seg">
            {(['All', 'Enabled', 'Disabled'] as StatusFilter[]).map(s => (
              <button key={s} type="button" className={statusFilter === s ? 'on' : ''} onClick={() => setStatusFilter(s)}>{statusFilterLabels[s]}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Quick Actions ── */}
      <div className="ops-tree-section ops-tree-quick-section">
        <label className="form-label">{copy.quickActions}</label>
        <div className="ops-tree-actions">
          <div className="ops-tree-actions-left">
            <button type="button" className="btn btn-ghost ops-tree-action-btn" onClick={applyReadOnlyPreset} disabled={disabled}>{copy.readOnlyPreset}</button>
            <button type="button" className="btn btn-ghost ops-tree-action-btn" onClick={enableShown} disabled={disabled}>{copy.enableShown}</button>
            <button type="button" className="btn btn-ghost ops-tree-action-btn" onClick={disableShown} disabled={disabled}>{copy.disableShown}</button>
          </div>
        </div>
      </div>

      {/* ── Operations Overview ── */}
      <div className="ops-tree-section ops-tree-section-ops">
        <label className="form-label ops-tree-ops-label">{copy.overview}</label>
        <div className="ops-tree-header">
          <div className="ops-tree-count-wrap">
            <span className="ops-tree-count">
              {copy.countEnabled(enabledCount, activeCount)}
              {/* " · " is punctuation glue between whole messages, not copy — the
                  prototype joins the same three segments this way (line 7572-7577). */}
              {shownOps.length < activeCount && (
                <> · {t('mambuMcpDetail.opsTree.countMatchFilters', { matched: shownOps.length })}</>
              )}
              {anyFilterActive && (
                <> · <a role="button" tabIndex={0}
                  onClick={clearFilters}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clearFilters() }
                  }}
                >{t('mambuMcpDetail.opsTree.clearFilters')}</a></>
              )}
            </span>
          </div>
          <button
            type="button"
            className="tabs-collapse-all ops-tree-expand-btn"
            onClick={toggleCollapseAll}
            title={anyCollapsed
              ? t('mambuMcpDetail.opsTree.expandAllTitle')
              : t('mambuMcpDetail.opsTree.collapseAllTitle')}
          >
            <ChevronsIcon direction={anyCollapsed ? 'down' : 'up'} />
            {' '}
            <span className="ops-tree-expand-label">
              {anyCollapsed ? t('common.expandAll') : t('common.collapseAll2')}
            </span>
          </button>
        </div>

        <div className="ops-tree" role="tree" aria-label={ariaLabel} ref={treeRef} onKeyDown={handleTreeKeyDown}>
          {isEmpty ? <div className="ops-tree-empty">{copy.noMatch}</div> : treeRows}
        </div>
      </div>
    </div>
  )
}
